

import { useMemo } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  haptic,
  setHapticsEnabled as syncLibHapticsEnabled,
  type HapticPattern,
} from '@/lib/haptics';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  description?: string;
  duration?: number;
  /** Optional haptic pattern fired once when the toast is added. */
  haptic?: HapticPattern;
}

interface UIState {
  // Toasts
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Loading states
  isPageLoading: boolean;
  setPageLoading: (loading: boolean) => void;

  // Modals
  activeModal: string | null;
  modalData: Record<string, unknown> | null;
  openModal: (modalId: string, data?: Record<string, unknown>) => void;
  closeModal: () => void;

  // Haptics preference (persisted; client-local, not server-synced)
  hapticsEnabled: boolean;
  setHapticsEnabled: (v: boolean) => void;
}

let toastId = 0;

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Toasts
      toasts: [],
      addToast: (toast) => {
        const id = `toast-${++toastId}`;
        const newToast = { ...toast, id };

        // Dedupe identical pending titles (retry-taps, StrictMode double
        // effects) and cap the visible queue at 3 — the bespoke banners this
        // system replaced were single-slot, so unbounded stacking is a
        // regression trap on mobile where the column grows over content.
        set((state) => {
          const kept = state.toasts.filter(
            (t) => !(t.type === toast.type && t.title === toast.title),
          );
          return { toasts: [...kept, newToast].slice(-3) };
        });

        // The cap drops the OLDEST (slice(-3) keeps the tail, and newToast is
        // always the tail), so every added toast is guaranteed to render —
        // deliberately chosen over dropping the newest, which would silently
        // swallow the most relevant message. That makes the buzz unconditional:
        // there is no path where a toast is queued but never shown. The lib
        // handles enabled/reduced-motion gating.
        if (toast.haptic) haptic(toast.haptic);

        // Auto-remove after duration — success/info clear fast (matching the
        // old 3s banners); error/warning linger for reading time.
        const duration =
          toast.duration ??
          (toast.type === 'error' || toast.type === 'warning' ? 5000 : 3000);
        if (duration > 0) {
          setTimeout(() => {
            set((state) => ({
              toasts: state.toasts.filter((t) => t.id !== id),
            }));
          }, duration);
        }
      },
      removeToast: (id) =>
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        })),
      clearToasts: () => set({ toasts: [] }),

      // Loading states
      isPageLoading: false,
      setPageLoading: (loading) => set({ isPageLoading: loading }),

      // Modals
      activeModal: null,
      modalData: null,
      openModal: (modalId, data = {}) =>
        set({ activeModal: modalId, modalData: data }),
      closeModal: () => set({ activeModal: null, modalData: null }),

      // Haptics preference
      hapticsEnabled: true,
      setHapticsEnabled: (v) => {
        // Keep the pure lib module in sync so non-React call sites gate too.
        syncLibHapticsEnabled(v);
        set({ hapticsEnabled: v });
      },
    }),
    {
      name: 'fantasy-prime-ui',
      storage: createJSONStorage(() => localStorage),
      // Persist ONLY the haptics preference — toasts/modals are transient.
      partialize: (state) => ({ hapticsEnabled: state.hapticsEnabled }),
      onRehydrateStorage: () => (state) => {
        // Sync the rehydrated preference into the pure haptics module.
        if (state) syncLibHapticsEnabled(state.hapticsEnabled);
      },
    }
  )
);

// Convenience hook for toasts.
//
// Selects the two actions individually rather than destructuring the whole
// store: `useUIStore()` with no selector subscribes the consumer to EVERY store
// write, so a single toast (add + auto-remove = 2 writes) used to re-render all
// 15 call sites — including the event page and WeekView, and through them the
// whole game-card slate. Zustand actions are created once, so these references
// are stable for the store's lifetime and the memo below never recomputes,
// which also keeps downstream useCallback/useEffect deps stable.
export const useToast = () => {
  const addToast = useUIStore((s) => s.addToast);
  const removeToast = useUIStore((s) => s.removeToast);

  return useMemo(() => ({
    toast: addToast,
    dismiss: removeToast,
    success: (
      title: string,
      description?: string,
      haptic: HapticPattern | null = 'success'
    ) =>
      addToast({
        type: 'success',
        title,
        description,
        ...(haptic ? { haptic } : {}),
      }),
    error: (
      title: string,
      description?: string,
      haptic: HapticPattern | null = 'error'
    ) =>
      addToast({
        type: 'error',
        title,
        description,
        ...(haptic ? { haptic } : {}),
      }),
    info: (title: string, description?: string, haptic?: HapticPattern) =>
      addToast({
        type: 'info',
        title,
        description,
        ...(haptic ? { haptic } : {}),
      }),
    warning: (
      title: string,
      description?: string,
      haptic: HapticPattern | null = 'warning'
    ) =>
      addToast({
        type: 'warning',
        title,
        description,
        ...(haptic ? { haptic } : {}),
      }),
  }), [addToast, removeToast]);
};
